// src/legal/TermsModal.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Props:
//   onAccept  — called when user clicks "I Accept". Omit when readOnly.
//   onClose   — called when user clicks ✕ (readOnly mode from SignUpScreen).
//   busy      — shows spinner on Accept button (AuthGate uses this).
//   readOnly  — shows only ✕ close; no Accept button (SignUpScreen preview).
// ─────────────────────────────────────────────────────────────────────────────
export default function TermsModal({ onAccept, onClose, busy = false, readOnly = false }) {
  return (
    <div style={overlayStyle} onClick={readOnly ? onClose : undefined}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, paddingBottom: 16, borderBottom: "1px solid #21262d" }}>
          <div>
            <h2 style={headingStyle}>Terms of Service</h2>
            <p style={{ color: "#8b949e", fontSize: 12, margin: 0 }}>Effective Date: June 2025</p>
          </div>
          {(readOnly || onClose) && (
            <button onClick={readOnly ? onClose : onClose} style={closeBtnStyle}>✕</button>
          )}
        </div>

        {/* Scrollable body */}
        <div style={bodyStyle}>

          <Section title="1. About Writing Coach">
            Writing Coach ("the App") is an AI-powered writing evaluation tool for students in Grades 2–12.
            It is operated as a product of Chaicodes.com ("we," "us," "our").
            By using the App you agree to these Terms. If you do not agree, do not use the App.
          </Section>

          <Section title="2. Eligibility & Parental Consent (COPPA)">
            The App is intended for students of all ages when used under appropriate supervision.
            {"\n\n"}
            <strong style={{ color: "#c9d1d9" }}>Users under 13:</strong> In compliance with the Children's Online Privacy Protection Act (COPPA),
            children under 13 may only use this App under the direct supervision of a parent, legal guardian, or
            licensed educator. By creating an account for or with a child under 13, you represent that you are
            that child's parent or guardian and consent to the collection and use of information as described
            in our Privacy Policy.
            {"\n\n"}
            <strong style={{ color: "#c9d1d9" }}>School / Institutional Use (FERPA):</strong> When Writing Coach is used in an educational
            institution, the school or district acts as the data controller and is responsible for obtaining
            any required consents from students and parents under the Family Educational Rights and Privacy Act (FERPA).
            The institution's authorized administrator agrees to these Terms on behalf of all users in that institution.
          </Section>

          <Section title="3. Accounts & Security">
            You are responsible for maintaining the confidentiality of your account credentials.
            You may not share your account or allow others to use your tokens. We reserve the right to
            suspend accounts that are shared, abused, or used in violation of these Terms.
          </Section>

          <Section title="4. Tokens & Payments">
            Writing Coach uses a token system. Each evaluation consumes 1 token. Tokens are non-refundable
            once used. Unused tokens do not expire. Tokens are shared across Writing Coach, SSE, and Reading
            Comp Buddy on the same account. Purchases are processed securely by Stripe; we do not store
            payment card details. All sales are final except where required by applicable law.
          </Section>

          <Section title="5. Student Writing & Privacy">
            <strong style={{ color: "#c9d1d9" }}>No permanent storage of student writing:</strong> Writing submitted for evaluation is
            transmitted to the Anthropic Claude API for processing and is not retained by Writing Coach after
            the evaluation is returned. Anthropic's data handling is governed by{" "}
            <a href="https://www.anthropic.com/privacy" target="_blank" rel="noopener noreferrer" style={linkStyle}>Anthropic's Privacy Policy</a>.
            {"\n\n"}
            We store only your account information (email, token balance) and transaction records in our
            secure database. We do not sell your personal information to third parties.
          </Section>

          <Section title="6. Acceptable Use">
            You may not use the App to submit writing that contains hate speech, threats, sexually explicit
            content, or any material that violates applicable law.
            {"\n\n"}
            Writing Coach is an evaluation tool, not a ghostwriting service. Submissions must be the
            student's own original work. Using the App to evaluate text generated, rewritten, or
            substantially produced by another AI tool — and presenting it as the student's own writing for
            a graded assignment — undermines the educational purpose of the App and is not supported or
            endorsed by Writing Coach or its creators.
            {"\n\n"}
            Misuse, including submission of plagiarized or AI-generated work passed off as original, may
            result in immediate account termination without refund.
          </Section>

          <Section title="7. Disclaimer & Limitation of Liability">
            Writing Coach provides AI-generated feedback for educational purposes only. It is not a substitute
            for professional instruction. We make no guarantees about evaluation accuracy. To the maximum extent
            permitted by law, our liability is limited to the amount you paid for tokens in the 30 days
            preceding any claim.
          </Section>

          <Section title="8. Changes to Terms">
            We may update these Terms from time to time. Continued use of the App after changes constitutes
            acceptance of the revised Terms. We will notify users of material changes via email.
          </Section>

          <Section title="9. Contact">
            Questions? Email us at <a href="mailto:support@chaicodes.com" style={linkStyle}>support@chaicodes.com</a>.
          </Section>
        </div>

        {/* Footer */}
        {!readOnly && onAccept && (
          <div style={{ paddingTop: 16, borderTop: "1px solid #21262d" }}>
            <p style={{ fontSize: 12, color: "#8b949e", marginBottom: 12, lineHeight: 1.6 }}>
              By clicking "I Accept" you confirm you have read and agree to these Terms of Service and our Privacy Policy,
              and that you meet the eligibility requirements above.
            </p>
            <button onClick={onAccept} disabled={busy} style={acceptBtnStyle(busy)}>
              {busy ? "Saving…" : "I Accept — Continue →"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Sub-component ─────────────────────────────────────────────────────────────
function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <h3 style={{ fontSize: 13, fontWeight: 700, color: "#e6edf3", margin: "0 0 6px" }}>{title}</h3>
      <p style={{ fontSize: 13, color: "#8b949e", margin: 0, lineHeight: 1.75, whiteSpace: "pre-wrap" }}>
        {children}
      </p>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const overlayStyle = {
  position:       "fixed",
  inset:          0,
  background:     "rgba(0,0,0,0.85)",
  display:        "flex",
  alignItems:     "center",
  justifyContent: "center",
  zIndex:         300,
  padding:        "20px",
};

const modalStyle = {
  width:        "100%",
  maxWidth:     580,
  maxHeight:    "90vh",
  background:   "#161b22",
  border:       "1px solid #30363d",
  borderRadius: 16,
  padding:      "28px",
  display:      "flex",
  flexDirection: "column",
  fontFamily:   "'DM Sans', sans-serif",
};

const headingStyle = {
  fontFamily: "'DM Serif Display', serif",
  fontSize:   22,
  fontWeight: 400,
  color:      "#e6edf3",
  margin:     0,
};

const bodyStyle = {
  overflowY: "auto",
  flex:      1,
  marginBottom: 8,
  paddingRight: 4,
};

const closeBtnStyle = {
  background:     "transparent",
  border:         "1px solid #30363d",
  color:          "#8b949e",
  borderRadius:   7,
  width:          32,
  height:         32,
  cursor:         "pointer",
  fontSize:       16,
  display:        "flex",
  alignItems:     "center",
  justifyContent: "center",
  flexShrink:     0,
};

const acceptBtnStyle = (busy) => ({
  width:        "100%",
  padding:      "12px",
  borderRadius: 9,
  fontSize:     14,
  fontWeight:   700,
  border:       "none",
  cursor:       busy ? "not-allowed" : "pointer",
  background:   busy ? "#21262d" : "linear-gradient(135deg, #1f6feb, #388bfd)",
  color:        busy ? "#484f58" : "#fff",
  fontFamily:   "'DM Sans', sans-serif",
});

const linkStyle = {
  color:          "#388bfd",
  textDecoration: "underline",
};
